import { CountriesType, countries, countryName } from 'src/domains/layouts/config/countries';
import { CustomKeyboardState } from 'src/domains/players/state/CustomKeyboardState';
import { DateInputState } from 'src/domains/players/webview/components/form/DateInput';
import {
    GenderTitleState,
    GenderTitleType,
} from 'src/domains/players/webview/components/SignUp/signupPrimary/signUpViews/createAccount/yourDetails/titleSection/TitleSection.state';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { observable, action, makeObservable } from 'mobx';
import { PhoneNumberState } from 'src/domains/players/webview/components/SignUp/signupParts/phoneNumber/PhoneNumber.state';
import { SelectState } from 'src/domains/players/webview/components/form/select/Select.state';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import {
    validateCity,
    validateFirstName,
    validateLastName,
    validateRequire,
    validateSpaces,
    validatePostCode,
    validateRequireIfWasTouched,
    validateAddress,
} from 'src/domains/players/webview/components/ValidatorsNew';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { DateTime } from 'src_common/utils/time/time';
import { GeolocalizationState } from 'src/domains/layouts/state/geolocalizationState/GeolocalizationState';
import { CurrencyType } from 'src_common/common/amount/website-money/currency';
import { TermsAndConditionsCheckboxState } from 'src/domains/players/webview/components/SignUp/signupParts/termsAndConditions/TermsAndConditionsCheckbox.state';
import { AddressSearchState } from 'src/domains/players/state/AddressSearchState';
import { findAddress } from 'src_server/trpc/types/accountModel';
import {
    ContactPreferencesNewformatFormModelType,
    OffersContactPreferencesNewState,
} from './offersSettingsNew/OffersSettingsNew.state';
import { Common } from 'src/domains/common/Common';

const excludeCountries = (array: CountriesType, excludeList: Array<string>): CountriesType => {
    return array.filter((country) => !excludeList.includes(country.id));
};

const includeCountries = (array: CountriesType, excludeList: Array<string>): CountriesType => {
    return array.filter((country) => excludeList.includes(country.id));
};

export interface YourDetailsFormType {
    firstName: string;
    lastName: string;
    postcode: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    country: string;
    currency: string;
    contactPreferences: ContactPreferencesNewformatFormModelType;
    dateOfBirth: DateTime;
    prefix: string;
    phoneNumber: string;
}

interface AddressFormGroupType {
    postcode: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    country: string;
}

export interface GenderFormModelType {
    genderTitle: GenderTitleType;
}
export class YourDetailsState {
    @observable public isShownFullAddressForm: boolean = false;
    @observable public loadingCreateAccountReq: boolean = false;
    @observable public wasTouched: boolean | null = null;
    @observable public validateOnSubmit: boolean = false;
    @observable public myAddressError: string | null = null;

    public readonly addressSearchState: AddressSearchState;
    public termsAndConditionsCheckboxState: TermsAndConditionsCheckboxState;
    public readonly firstNameState: FormInputState<string, string>;
    public readonly lastNameState: FormInputState<string, string>;
    public readonly postcodeState: FormInputState<string, string>;
    public readonly addressLineOneState: FormInputState<string, string>;
    public readonly addressLineTwoState: FormInputState<string, string>;
    public readonly cityState: FormInputState<string, string>;
    public readonly country: SelectState;
    public readonly currency: SelectState;
    public readonly addressFormGroup: FormModel<AddressFormGroupType>;
    public readonly contactPreferences: OffersContactPreferencesNewState;
    public readonly dateOfBirth: DateInputState;
    public readonly genderTitle: GenderTitleState;
    public readonly phoneNumber: PhoneNumberState;
    public readonly yourDetailsFormModel: FormModel<YourDetailsFormType>;
    public readonly genderTitleForModel: FormModel<GenderFormModelType>;

    public constructor(
        public readonly customKeyboard: CustomKeyboardState,
        private readonly config: ConfigComponents,
        private readonly common: Common
    ) {
        makeObservable(this);
        const geolocalization = GeolocalizationState.get(this.common);
        this.addressSearchState = new AddressSearchState(common.trpcClient);
        this.firstNameState = FormInputState.new('').map(validateRequire).map(validateFirstName);
        this.lastNameState = FormInputState.new('').map(validateRequire).map(validateLastName);
        const language = LanguagesState.get(this.common);
        this.postcodeState = FormInputState.new('').map(validatePostCode(language.getTranslation));

        this.addressLineOneState = FormInputState.new('').map(validateRequire).map(validateAddress);
        this.addressLineTwoState = FormInputState.new('').map(validateAddress);
        this.cityState = FormInputState.new('').map(validateRequire).map(validateCity).map(validateSpaces);

        const defaultCountryConfig = this.config.config.countrySignUpDefault;
        const countryOnlyUKDefault = this.config.config.countryOnlyUKDefault;
        const geolocalizationCountry = geolocalization.countryCode;

        const defaultCountryCode = countryOnlyUKDefault ? 'GB' : geolocalizationCountry ?? defaultCountryConfig;

        const defaultCountryValue =
            defaultCountryCode === null ? undefined : { [defaultCountryCode]: countryName[defaultCountryCode] };

        this.country = new SelectState(defaultCountryValue);
        this.phoneNumber = new PhoneNumberState(language, this.config.config.prefixAndPhoneNumberDefault);

        if (config.config.zipCodeAndCountryCheck === true) {
            const validator = (value: string): Result<string> =>
                validateRequireIfWasTouched(value, this.wasTouched === true || this.validateOnSubmit);
            this.country.inputState = this.country.inputState.map(validator);
            this.phoneNumber.prefix.inputState = this.phoneNumber.prefix.inputState.map(validator);
        }

        const currencyGeolocalization = geolocalization.currencyCode;
        const defaultCurrencyValue =
            currencyGeolocalization === undefined ? this.config.config.currencyDefault : currencyGeolocalization;
        const defaultCurrency = this.config.config.currencyDefaultSelectedInSignup
            ? { currency: defaultCurrencyValue }
            : undefined;
        this.currency = new SelectState(defaultCurrency, true);
        this.contactPreferences = new OffersContactPreferencesNewState();
        this.dateOfBirth = new DateInputState(this.customKeyboard);
        this.genderTitle = new GenderTitleState(this.config.config);
        this.termsAndConditionsCheckboxState = new TermsAndConditionsCheckboxState(this.config.config);

        this.yourDetailsFormModel = FormModel.group({
            firstName: this.firstNameState,
            lastName: this.lastNameState,
            postcode: this.postcodeState,
            addressLine1: this.addressLineOneState,
            addressLine2: this.addressLineTwoState,
            city: this.cityState,
            country: this.country.inputState,
            contactPreferences: this.contactPreferences.formatFormModel,
            dateOfBirth: this.dateOfBirth.dateModel,
            phoneNumber: this.phoneNumber.phoneNumber,
            prefix: this.phoneNumber.prefix.inputState,
            currency: this.currency.inputState,
        });
        this.genderTitleForModel = FormModel.group({
            genderTitle: this.genderTitle.titleChooseState,
        });

        this.addressFormGroup = FormModel.group({
            postcode: this.postcodeState,
            addressLine1: this.addressLineOneState,
            addressLine2: this.addressLineTwoState,
            city: this.cityState,
            country: this.country.inputState,
        });
    }

    @action public showFullAddressFormHandler = (): void => {
        this.isShownFullAddressForm = true;
        this.myAddressError = null;
        this.addressSearchState.clearAddresses();
        this.addressSearchState.clearError();
    };

    @action public clearAddressForm = (): void => {
        this.addressFormGroup.reset();
        this.isShownFullAddressForm = false;
    };

    @action public handleClickOutside = (clickOutside: boolean): void => {
        if (clickOutside === true) {
            this.addressSearchState.clearAddresses();
        }
    };

    @action public handleWasTouched = (): void => {
        this.wasTouched = true;
    };

    public onSearchAddressHandler = async (): Promise<void> => {
        await this.addressSearchState.searchAddress(this.postcodeState.value);
        this.syncAddressError();
    };

    public setAddressFromPostcode = async (address: findAddress.AddressItemType): Promise<void> => {
        await this.addressSearchState.selectAddress(address, (details) => {
            this.showFullAddressFormHandler();

            const countryFromPostcode = Object.entries(countryName).filter(
                (country) => country[0] === details.countryIso2
            )[0];

            if (countryFromPostcode === undefined) {
                this.myAddressError = 'Unknown country code';
                return;
            }

            this.addressLineOneState.setValue(details.line1);
            this.addressLineOneState.setAsVisited();
            this.addressLineTwoState.setValue(details.line2);
            this.addressLineTwoState.setAsVisited();
            this.cityState.setValue(details.city);
            this.cityState.setAsVisited();
            this.postcodeState.setValue(details.postalCode);
            this.postcodeState.setAsVisited();

            const [countryCode, countryDisplayName] = countryFromPostcode;
            this.country.setCurrentValue({ [countryCode]: countryDisplayName });

            if (this.country.inputState.value.toLowerCase() === 'ireland') {
                const defIrelandCurrency: CurrencyType = 'EUR';
                this.currency.setCurrentValue({ currency: defIrelandCurrency });
            }
        });
        this.syncAddressError();
    };

    @action private syncAddressError = (): void => {
        this.myAddressError = this.addressSearchState.addressError;
    };

    public formattedCountries = (): Array<Record<string, string>> => {
        const sortedCountries = countries.sort((a, b) => a.name.localeCompare(b.name));
        if (this.config.config.onlyCountriesForSignUp.length > 0) {
            const countriesAfterInclude = includeCountries(sortedCountries, this.config.config.onlyCountriesForSignUp);
            const sortedCountriesFormatted = countriesAfterInclude.map((country) => ({ [country.id]: country.name }));
            return sortedCountriesFormatted;
        }
        const countriesAfterExclude = excludeCountries(sortedCountries, this.config.config.excludedCountriesFromSignUp);
        const sortedCountriesFormatted = countriesAfterExclude.map((country) => ({ [country.id]: country.name }));
        return sortedCountriesFormatted;
    };
}
